import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import type { VorankBegleitblattEingabe } from '../vorank/vorankUnterschriftTypes';
import { VORANK_ERLAEUTERUNG_UNTERSCHRIFT, erlaeuterungenFuer } from '../../data/vorankErlaeuterungen';
import { vorankBerlinDatum } from './generatorVorank';
import { winAnsiSicher } from './winAnsi';

/**
 * Begleitblatt zur Vorankündigung (docs/vorankuendigung-unterschrift-plan.md §5).
 *
 * Wird an ein FERTIGES Dokument aus `generateVorankuendigungPDF` angehängt —
 * dessen „Seite x/y“ ist dann schon gestempelt, die Vorankündigung bleibt
 * „1/2, 2/2“. Das Begleitblatt zählt eigene Seiten („Begleitblatt — Seite 1/1“)
 * und geht nur an den Bauherrn, nie an die Behörde; der Plot hängt es nie an.
 *
 * Aufbau: Kopf mit Warnhinweis, Baustelle und Fassung; rechts daneben der
 * QR-Kasten zur digitalen Unterschrift (nur mit Link); darunter die
 * Erläuterungen je Punkt und zuletzt der Papierweg. Ohne Link (SQL fehlt,
 * Zeitbudget überschritten) entfällt nur der QR-Kasten.
 *
 * Fließsatz wie im Generator: passt ein Absatz nicht mehr auf die Seite,
 * beginnt eine Folgeseite.
 */

const RAND = 20;
const SEITE_B = 210;
const INHALT_B = SEITE_B - 2 * RAND;
const RECHTS = SEITE_B - RAND;
/** Unterkante des Fließsatzes; darunter steht nur die Fußzeile. */
const INHALT_UNTEN = 279;
/** Oberkante des Fließsatzes auf Folgeseiten. */
const INHALT_OBEN_FOLGE = 31;
const FUSS_Y = 288;

/** QR-Kasten oben rechts. */
const KASTEN_B = 84;
const KASTEN_X = RECHTS - KASTEN_B;
const KASTEN_PAD = 4;
const QR_MM = 35;

/** Erläuterungen: Nummer | Titel | Text. */
const NR_X = RAND;
const TITEL_X = RAND + 7;
const TITEL_B = 40;
const TEXT_X = TITEL_X + TITEL_B + 3;
const TEXT_B = RECHTS - TEXT_X;
const ZEILE = 3.75;

const WARNROT: [number, number, number] = [170, 30, 30];
const LINKBLAU: [number, number, number] = [25, 70, 150];

/** Höchstens so viele Zeilen je Angabe im Kopf (Baustelle, Bauherr). */
const ANGABE_MAX_ZEILEN = 6;

const UEBERSCHRIFT = 'Begleitblatt zur Vorankündigung';
const WARNUNG = 'Für den Bauherrn, nicht an die Behörde senden';

/**
 * Geschützte Leerzeichen, wo ein Umbruch den Sinn zerreißt („§ 4“, „z. B.“,
 * „7 m“). jsPDF misst das geschützte Leerzeichen etwas breiter, als es
 * gesetzt wird — die Zeile bricht dann eher früher um, läuft aber nie über.
 */
const zusammenhalten = (text: string): string =>
    text
        .replace(/z\. B\./g, 'z. B.')
        .replace(/§ (\d)/g, '§ $1')
        .replace(/(\d) (m|Arbeitstage|Beschäftigte|Personentage|BaustellV)\b/g, '$1 $2');

/** QR als PNG-data-URL; ein Fehler kostet nur das Bild, nicht das Begleitblatt. */
const qrBild = async (url: string): Promise<string | null> => {
    try {
        return await QRCode.toDataURL(url, { width: 512, margin: 1, errorCorrectionLevel: 'M' });
    } catch (e) {
        console.warn('Vorankündigung: QR-Code für das Begleitblatt ließ sich nicht erzeugen — der Link steht im Klartext.', e);
        return null;
    }
};

/** Satz für den Papierweg — mit Rücksendeadresse, wenn eine bekannt ist. */
export const papierwegSatz = (a: { mitLink: boolean; unterschriftSeite: number; kontaktMail?: string | null }): string => {
    const mail = (a.kontaktMail || '').trim();
    const seite = a.unterschriftSeite >= 1 ? `auf Seite ${a.unterschriftSeite}` : 'auf der letzten Seite';
    const anfang = a.mitLink ? 'Alternativ: ausdrucken' : 'Bitte ausdrucken';
    return `${anfang}, ${seite} unterschreiben und als PDF oder Foto ${mail ? `an ${mail} ` : ''}zurücksenden.`;
};

export const haengeBegleitblattAn = async (doc: jsPDF, e: VorankBegleitblattEingabe): Promise<void> => {
    // Die Seitenzahl der Vorankündigung steht fest, bevor das Begleitblatt
    // beginnt: auf ihrer letzten Seite wird unterschrieben.
    const vorankSeiten = doc.getNumberOfPages();
    const qr = e.link ? await qrBild(e.link.url) : null;

    const schrift = (stil: 'normal' | 'bold', groesse: number, farbe: number | [number, number, number] = 0) => {
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        if (Array.isArray(farbe)) doc.setTextColor(farbe[0], farbe[1], farbe[2]);
        else doc.setTextColor(farbe);
    };
    /** Umbrechen in der GERADE gesetzten Schrift — erst Schrift setzen, dann messen. */
    const umbrechen = (text: string, breite: number): string[] =>
        doc.splitTextToSize(winAnsiSicher(text), breite) as string[];
    /** Zeilenweise setzen: Zeilenabstand in mm, unabhängig von jsPDFs lineHeightFactor. */
    const setzeZeilen = (zeilen: string[], x: number, yErste: number, abstand: number) => {
        zeilen.forEach((z, i) => doc.text(z, x, yErste + i * abstand));
    };
    const regel = (yLinie: number, grau = 190, staerke = 0.2) => {
        doc.setDrawColor(grau);
        doc.setLineWidth(staerke);
        doc.line(RAND, yLinie, RECHTS, yLinie);
    };

    let y = 0;
    const neueSeite = () => {
        doc.addPage('a4', 'portrait');
        schrift('bold', 10);
        doc.text(`${UEBERSCHRIFT} (Fortsetzung)`, RAND, 20);
        schrift('bold', 8, WARNROT);
        doc.text(WARNUNG, RECHTS, 20, { align: 'right' });
        regel(23);
        y = INHALT_OBEN_FOLGE;
    };
    const platz = (noetig: number) => {
        if (y + noetig > INHALT_UNTEN) neueSeite();
    };

    // -----------------------------------------------------------------------
    // Kopf
    // -----------------------------------------------------------------------
    doc.addPage('a4', 'portrait');
    schrift('bold', 16);
    doc.text(UEBERSCHRIFT, RAND, 21);
    schrift('bold', 10, WARNROT);
    doc.text(WARNUNG, RAND, 27.5);
    regel(31);

    const reiheOben = 38;

    // Links: worum es geht. Neben dem QR-Kasten schmal, ohne ihn über die ganze Breite.
    const linksB = e.link ? KASTEN_X - RAND - 7 : INHALT_B;
    let yl = reiheOben;
    const angabe = (bezeichnung: string, wert: string) => {
        if (!wert.trim()) return;
        schrift('normal', 7, 110);
        doc.text(bezeichnung, RAND, yl);
        yl += 3.9;
        schrift('normal', 9.5);
        // Der Kopf fließt nicht um: eine Angabe darf ihn nicht über die Seite
        // drücken. Vollständig steht sie in der Vorankündigung selbst.
        const alle = umbrechen(wert.trim(), linksB);
        const zeilen = alle.length > ANGABE_MAX_ZEILEN
            ? [...alle.slice(0, ANGABE_MAX_ZEILEN - 1), `${alle[ANGABE_MAX_ZEILEN - 1]} …`]
            : alle;
        setzeZeilen(zeilen, RAND, yl, 4.3);
        yl += (zeilen.length - 1) * 4.3 + 6;
    };
    angabe('Baustelle', e.data.siteAddress || '');
    angabe('Bauherr', e.data.clientName || '');
    angabe('Fassung vom', vorankBerlinDatum(e.fassungVom));

    yl += 1;
    schrift('normal', 9);
    const einleitung = umbrechen(
        'Bitte prüfen Sie die Angaben der Vorankündigung und bestätigen Sie sie mit Ihrer Unterschrift. '
        + 'Unten steht zu jedem Punkt, was er bedeutet.',
        linksB,
    );
    setzeZeilen(einleitung, RAND, yl, 4.2);
    yl += (einleitung.length - 1) * 4.2;

    // Rechts: der QR-Kasten.
    let yr = reiheOben;
    if (e.link) {
        const kastenOben = reiheOben - 5;
        const innenX = KASTEN_X + KASTEN_PAD;
        const innenB = KASTEN_B - 2 * KASTEN_PAD;

        const titelY = kastenOben + KASTEN_PAD + 3.8;
        const qrY = titelY + 3;
        const stufenX = qr ? innenX + QR_MM + 4 : innenX;
        const stufenB = KASTEN_X + KASTEN_B - KASTEN_PAD - stufenX;

        schrift('normal', 8);
        const schritte = ['QR scannen oder Link öffnen', 'Angaben prüfen', 'Unterschreiben']
            .map(s => umbrechen(s, stufenB - 4));
        schrift('normal', 7);
        const linkZeilen = umbrechen(e.link.url, innenB);
        const gueltig = vorankBerlinDatum(e.link.gueltigBis);

        const stufenH = 4 + schritte.reduce((s, z) => s + (z.length - 1) * 3.5 + 5, 0) + (gueltig ? 3 : 0);
        const mitteH = qr ? Math.max(QR_MM, stufenH) : stufenH;
        const linkY = qrY + mitteH + 4.2;
        const kastenUnten = linkY + (linkZeilen.length - 1) * 3.1 + KASTEN_PAD;

        doc.setDrawColor(150);
        doc.setLineWidth(0.3);
        doc.roundedRect(KASTEN_X, kastenOben, KASTEN_B, kastenUnten - kastenOben, 2, 2, 'S');

        schrift('bold', 10.5);
        doc.text('Digital unterschreiben', innenX, titelY);

        if (qr) {
            try {
                doc.addImage(qr, 'PNG', innenX, qrY, QR_MM, QR_MM);
            } catch (fehler) {
                console.warn('Vorankündigung: QR-Code ließ sich nicht ins Begleitblatt einbetten.', fehler);
            }
        }

        // Die drei Schritte, nummeriert, rechts neben dem QR.
        let ys = qrY + 4;
        schritte.forEach((zeilen, i) => {
            schrift('bold', 8);
            doc.text(`${i + 1}`, stufenX, ys);
            schrift('normal', 8);
            setzeZeilen(zeilen, stufenX + 4, ys, 3.5);
            ys += (zeilen.length - 1) * 3.5 + 5;
        });
        if (gueltig) {
            schrift('normal', 7.5, 100);
            doc.text(`Link gültig bis ${gueltig}`, stufenX, ys);
        }

        schrift('normal', 7, LINKBLAU);
        setzeZeilen(linkZeilen, innenX, linkY, 3.1);
        // Am Rechner geöffnet, sind QR und Link anklickbar.
        doc.link(innenX, qrY, innenB, linkY + (linkZeilen.length - 1) * 3.1 + 1 - qrY, { url: e.link.url });

        yr = kastenUnten;
    }

    y = Math.max(yl, yr) + 11;

    // -----------------------------------------------------------------------
    // Erläuterungen
    // -----------------------------------------------------------------------
    platz(20);
    schrift('bold', 11);
    doc.text('Erläuterungen zu den Punkten der Vorankündigung', RAND, y);
    y += 2.2;
    regel(y, 120, 0.3);
    y += 5.2;

    const absatz = (nummer: string, titel: string, text: string) => {
        schrift('bold', 8.5);
        const titelZeilen = umbrechen(titel, TITEL_B);
        schrift('normal', 8.5);
        const textZeilen = umbrechen(zusammenhalten(text), TEXT_B);
        const zeilen = Math.max(titelZeilen.length, textZeilen.length);
        // Ein Absatz wird nicht zerrissen — er ist kurz genug für die nächste Seite.
        platz((zeilen - 1) * ZEILE + 2);

        schrift('bold', 8.5);
        if (nummer) doc.text(`${nummer}.`, NR_X, y);
        setzeZeilen(titelZeilen, TITEL_X, y, ZEILE);
        schrift('normal', 8.5, 35);
        setzeZeilen(textZeilen, TEXT_X, y, ZEILE);
        y += (zeilen - 1) * ZEILE + 2.3;
        doc.setDrawColor(225);
        doc.setLineWidth(0.15);
        doc.line(RAND, y, RECHTS, y);
        y += 4.2;
    };

    erlaeuterungenFuer(e.data).forEach(p => absatz(p.punkt, p.titel, p.text));
    absatz('', 'Unterschrift', VORANK_ERLAEUTERUNG_UNTERSCHRIFT);

    // -----------------------------------------------------------------------
    // Papierweg — grau hinterlegt, damit er neben dem QR-Kasten nicht untergeht.
    // -----------------------------------------------------------------------
    schrift('normal', 9);
    const papier = umbrechen(
        papierwegSatz({ mitLink: !!e.link, unterschriftSeite: vorankSeiten, kontaktMail: e.kontaktMail }),
        INHALT_B - 8,
    );
    y += 1.5;
    const papierH = (papier.length - 1) * 4.3 + 7.6;
    platz(papierH);
    doc.setFillColor(241, 243, 245);
    doc.roundedRect(RAND, y - 4.6, INHALT_B, papierH, 1.5, 1.5, 'F');
    schrift('normal', 9);
    setzeZeilen(papier, RAND + 4, y + 0.4, 4.3);

    // -----------------------------------------------------------------------
    // Eigene Fußzeile — die Vorankündigung behält ihr „Seite x/y“.
    // -----------------------------------------------------------------------
    const gesamt = doc.getNumberOfPages() - vorankSeiten;
    for (let i = 1; i <= gesamt; i++) {
        doc.setPage(vorankSeiten + i);
        schrift('normal', 8, 150);
        doc.text(`Begleitblatt — Seite ${i}/${gesamt}`, RECHTS, FUSS_Y, { align: 'right' });
        doc.text('Nicht an die Behörde senden', RAND, FUSS_Y);
    }
    doc.setTextColor(0);
};
